// src/pages/FiadosPage.tsx
import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import { api } from "../services/api";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";

/* -------------------------------------
   Interfaces de tipos
-------------------------------------- */
interface ItemFiado {
  nome: string;
  quantidade: number;
  precoUnitario: number;
  precoComDesconto: number;
  subtotal: number;
}

interface VendaResumo {
  id: number;
  valorTotal: number;
  dataHora: string;
  nomeCompradorFiado?: string | null;
  itens: ItemFiado[];
}

interface FiadoGrupo {
  equipeId: number | null;
  nome: string;
  quantidadeVendas: number;
  total: number;
  vendas: VendaResumo[];
}

interface FormaPagamento {
  id: number;
  nome: string;
}

interface Evento {
  id: number;
  descricao: string;
  ativo: boolean;
}

/* -------------------------------------
   Componente principal
-------------------------------------- */
const FiadosPage: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const eventoIdParam = queryParams.get("eventoId");

  const [eventoAtual, setEventoAtual] = useState<Evento | null>(null);
  const [fiados, setFiados] = useState<FiadoGrupo[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [grupoSelecionado, setGrupoSelecionado] = useState<FiadoGrupo | null>(null);
  const [selectedFormaPagamento, setSelectedFormaPagamento] = useState<number | null>(null);
  const [recebendo, setRecebendo] = useState(false);

  const [expandedGrupo, setExpandedGrupo] = useState<number | null>(null);
  const [expandedVenda, setExpandedVenda] = useState<number | null>(null);

  const formatCurrency = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  /* -------------------------------------
     Loaders
  -------------------------------------- */
  const carregarEvento = useCallback(async () => {
    try {
      if (eventoIdParam) {
        const resp = await api.get<Evento>(`/evento/${eventoIdParam}`);
        setEventoAtual(resp.data);
        return;
      }
      const resp = await api.get<Evento[]>("/evento");
      setEventoAtual(resp.data.find((e) => e.ativo) ?? null);
    } catch {
      Swal.fire("Erro", "Não foi possível carregar o evento.", "error");
    }
  }, [eventoIdParam]);

  const loadFormasPagamento = useCallback(async () => {
    try {
      const res = await api.get<FormaPagamento[]>("/formaPagamento");
      setFormasPagamento(res.data);
      if (res.data.length > 0) setSelectedFormaPagamento(res.data[0].id);
    } catch {
      Swal.fire("Erro", "Falha ao carregar formas de pagamento.", "error");
    }
  }, []);

  const loadFiados = useCallback(async () => {
    try {
      setLoading(true);
      const eventoId = eventoIdParam ?? eventoAtual?.id ?? "";
      const url = `/vendas/fiado-agrupado?eventoId=${eventoId}`;
      const res = await api.get<FiadoGrupo[]>(url);
      setFiados(res.data || []);
    } catch {
      Swal.fire("Erro", "Falha ao carregar fiados agrupados.", "error");
    } finally {
      setLoading(false);
    }
  }, [eventoAtual, eventoIdParam]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([carregarEvento(), loadFormasPagamento()]);
    };
    init();
  }, [carregarEvento, loadFormasPagamento]);

  useEffect(() => {
    if (eventoAtual) loadFiados();
  }, [eventoAtual, loadFiados]);

  /* -------------------------------------
     Ações
  -------------------------------------- */
  const abrirModalReceber = (grupo: FiadoGrupo) => {
    setGrupoSelecionado(grupo);
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setGrupoSelecionado(null);
  };

  const confirmarRecebimento = async () => {
    if (!grupoSelecionado) return;
    if (!selectedFormaPagamento)
      return Swal.fire("Atenção", "Escolha a forma de pagamento.", "warning");

    try {
      setRecebendo(true);

      await api.put("/vendas/receber-fiado-agrupado", {
        equipeId: grupoSelecionado.equipeId,
        formaPagamentoId: selectedFormaPagamento,
      });

      Swal.fire("Sucesso", "Fiado recebido!", "success");

      fecharModal();
      await loadFiados();
      await carregarEvento();
    } catch (err) {
      Swal.fire("Erro", "Falha ao receber fiado.", "error");
    } finally {
      setRecebendo(false);
    }
  };

  /* -------------------------------------
     Render
  -------------------------------------- */
  if (loading) {
    return (
      <div className="d-flex">
        <Sidebar />
        <div className="flex-grow-1 p-4 text-center" style={{ marginLeft: 220 }}>
          <div className="spinner-border text-success" style={{ width: 60, height: 60 }} />
          <p className="mt-3">Carregando fiados...</p>
        </div>
      </div>
    );
  }

  if (!eventoAtual) {
    return (
      <div className="d-flex">
        <Sidebar />
        <div className="flex-grow-1 p-4" style={{ marginLeft: 220 }}>
          <div className="alert alert-warning">Nenhum evento ativo encontrado.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex">
      <Sidebar />

      <div className="flex-grow-1 p-4" style={{ marginLeft: 220 }}>
        <div className="d-flex justify-content-between mb-3">
          <h2>📌 Fiados Agrupados — {eventoAtual.descricao}</h2>

          <button className="btn btn-outline-secondary" onClick={loadFiados}>
            Atualizar
          </button>
        </div>

        {/* LISTA AGRUPADA */}
        <div className="card shadow-sm p-3">
          {fiados.length === 0 && (
            <p className="text-muted">Nenhum fiado pendente para este evento.</p>
          )}

          {fiados.length > 0 &&
            fiados.map((g, idx) => (
              <div key={`grupo-${g.equipeId}-${idx}`} className="mb-3 border rounded p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1">{g.nome}</h5>
                    <small className="text-muted">
                      {g.quantidadeVendas} vendas — Total {formatCurrency(g.total)}
                    </small>
                  </div>

                  <div>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() =>
                        setExpandedGrupo(expandedGrupo === idx ? null : idx)
                      }
                    >
                      {expandedGrupo === idx ? "Recolher" : "Detalhes"}
                    </button>

                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => abrirModalReceber(g)}
                    >
                      Receber
                    </button>
                  </div>
                </div>

                {/* EXPANDE VENDAS */}
                {expandedGrupo === idx && (
                  <div className="mt-3 ps-3 border-start">
                    {g.vendas.map((v) => (
                      <div key={`venda-${v.id}`} className="mb-2">
                        <div
                          className="d-flex justify-content-between"
                          onClick={() =>
                            setExpandedVenda(expandedVenda === v.id ? null : v.id)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <div>
                            <strong>
                              Venda #{v.id}
                              {v.nomeCompradorFiado ? ` — ${v.nomeCompradorFiado}` : ""}
                            </strong>{" "}
                            <small className="text-muted">
                              {new Date(v.dataHora).toLocaleString("pt-BR")}
                            </small>
                          </div>
                          <strong>{formatCurrency(v.valorTotal)}</strong>
                        </div>

                        {/* ITENS */}
                        {expandedVenda === v.id && (
                          <div className="mt-2 ps-3">
                            {v.itens.map((i, iidx) => (
                              <div key={`item-${v.id}-${iidx}`}>
                                {i.quantidade}× {i.nome} —{" "}
                                {formatCurrency(i.subtotal)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>

        {/* MODAL RECEBIMENTO */}
        {showModal && grupoSelecionado && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">
                    Receber Fiado — {grupoSelecionado.nome}
                  </h5>
                  <button className="btn-close" onClick={fecharModal}></button>
                </div>

                <div className="modal-body">
                  <p>
                    Total do grupo:{" "}
                    <strong>{formatCurrency(grupoSelecionado.total)}</strong>
                  </p>

                  <h6>Forma de pagamento</h6>

                  {formasPagamento.map((f) => (
                    <div className="form-check" key={f.id}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="fp"
                        checked={selectedFormaPagamento === f.id}
                        onChange={() => setSelectedFormaPagamento(f.id)}
                      />
                      <label className="form-check-label">{f.nome}</label>
                    </div>
                  ))}
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={fecharModal}>
                    Cancelar
                  </button>

                  <button
                    className="btn btn-success"
                    onClick={confirmarRecebimento}
                    disabled={recebendo}
                  >
                    {recebendo ? "Processando..." : "Confirmar"}
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FiadosPage;
